import React, { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  FileText,
  Images,
  Mic,
  CloudUpload,
  X,
  Gauge as GaugeIcon,
  ShieldAlert,
  Square,
  Loader2,
} from "lucide-react";
import { NavBar } from "../components/NavBar";
import { ScanOverlay } from "../components/ScanOverlay";
import { analyze, transcribe } from "../lib/api";
import { analysisStore } from "../lib/store";
import { useEntitlements } from "../lib/entitlements";

const TACTICS = [
  "Gaslighting",
  "DARVO",
  "Projection",
  "Blame-shifting",
  "Stonewalling",
  "Love-bombing",
  "Invalidation",
  "Playing the victim",
];

// iOS forces all third-party browsers (Chrome/Edge/Firefox/Opera/Google app) onto
// Apple's WKWebView, which blocks web getUserMedia for the mic with no prompt. Only
// Safari can record in-page on iOS. Detect this so we route to a working fallback.
const UA = typeof navigator !== "undefined" ? navigator.userAgent : "";
const IS_IOS =
  /iPad|iPhone|iPod/.test(UA) ||
  (typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const IS_IOS_NON_SAFARI = IS_IOS && /(CriOS|FxiOS|EdgiOS|OPiOS|GSA)/.test(UA);

export default function Home() {
  const navigate = useNavigate();
  const { refresh } = useEntitlements();
  const [mode, setMode] = useState("text");
  const [transcript, setTranscript] = useState("");
  const [personA, setPersonA] = useState("");
  const [personB, setPersonB] = useState("");
  const [images, setImages] = useState([]); // {url, base64}
  const [loading, setLoading] = useState(false);

  const fileRef = useRef(null);
  const audioFileRef = useRef(null);

  // voice
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [review, setReview] = useState(null); // { blob, filename, url }
  const mediaRec = useRef(null);
  const chunks = useRef([]);
  const recTimer = useRef(null);
  const recStart = useRef(0);
  const reviewUrlRef = useRef(null);

  const onPickFiles = useCallback(async (fileList) => {
    const files = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    const read = (file) =>
      new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          const base64 = String(dataUrl).split(",")[1];
          resolve({ url: dataUrl, base64 });
        };
        reader.readAsDataURL(file);
      });
    const picked = await Promise.all(files.map(read));
    setImages((prev) => [...prev, ...picked].slice(0, 6));
  }, []);

  const pickRecordingType = () => {
    // iOS Safari records audio/mp4; Chrome/Firefox/Android record webm. Pick what the
    // device actually supports so the Blob + filename extension match (whisper needs it).
    const candidates = [
      ["audio/webm;codecs=opus", "webm"],
      ["audio/webm", "webm"],
      ["audio/mp4", "m4a"],
      ["audio/mp4;codecs=mp4a.40.2", "m4a"],
      ["audio/aac", "m4a"],
    ];
    const canCheck = typeof window.MediaRecorder !== "undefined" && typeof window.MediaRecorder.isTypeSupported === "function";
    for (const [type, ext] of candidates) {
      if (canCheck && window.MediaRecorder.isTypeSupported(type)) return { type, ext };
    }
    return { type: "", ext: "m4a" }; // let the browser choose (Safari default = mp4)
  };

  const extForBlobType = (t, fallback) => {
    if (!t) return fallback;
    if (t.includes("webm")) return "webm";
    if (t.includes("mp4") || t.includes("m4a") || t.includes("aac") || t.includes("mpeg")) return "m4a";
    if (t.includes("ogg")) return "ogg";
    if (t.includes("wav")) return "wav";
    return fallback;
  };

  const stopTimer = () => {
    if (recTimer.current) {
      clearInterval(recTimer.current);
      recTimer.current = null;
    }
  };

  const toReview = (blob, filename) => {
    try {
      if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    } catch {}
    const url = URL.createObjectURL(blob);
    reviewUrlRef.current = url;
    setReview({ blob, filename, url });
  };

  const startRecording = async () => {
    const md = navigator.mediaDevices;
    // Diagnostic (no audio/content logged).
    console.info("[mic] start tap", {
      secure: window.isSecureContext,
      hasMediaDevices: !!md,
      hasGetUserMedia: !!(md && md.getUserMedia),
      hasMediaRecorder: typeof window.MediaRecorder !== "undefined",
      ua: navigator.userAgent,
    });

    // Unsupported browser / insecure context: use the device recorder via file input.
    if (!window.isSecureContext || !md || typeof md.getUserMedia !== "function" || typeof window.MediaRecorder === "undefined") {
      toast("Opening your device recorder…");
      audioFileRef.current?.click();
      return;
    }
    // Chrome/Edge/Firefox on iPhone cannot grant mic access (WKWebView blocks it with no
    // prompt). Skip the doomed getUserMedia call and go straight to pick/record a file.
    if (IS_IOS_NON_SAFARI) {
      toast("In-page recording isn't supported in this browser on iPhone — opening your files to pick a recording. Tip: open narccheckai.com in Safari to record here.");
      audioFileRef.current?.click();
      return;
    }
    try {
      const stream = await md.getUserMedia({ audio: true });
      const { type, ext } = pickRecordingType();
      let rec;
      try {
        rec = type ? new MediaRecorder(stream, { mimeType: type }) : new MediaRecorder(stream);
      } catch {
        rec = new MediaRecorder(stream); // some devices reject explicit mimeType
      }
      chunks.current = [];
      rec.ondataavailable = (e) => e.data && e.data.size > 0 && chunks.current.push(e.data);
      rec.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        stopTimer();
        const blobType = rec.mimeType || type || "audio/webm";
        const blob = new Blob(chunks.current, { type: blobType });
        setRecording(false);
        if (blob.size === 0) {
          toast.error("Nothing was recorded. Please try again.");
          return;
        }
        toReview(blob, `recording.${extForBlobType(blobType, ext)}`);
      };
      rec.onerror = (ev) => {
        console.error("[mic] MediaRecorder error", ev?.error?.name || ev?.error || ev);
        toast.error("Recording failed. Please try again or upload an audio file.");
        stopTimer();
        setRecording(false);
      };
      rec.start();
      mediaRec.current = rec;
      recStart.current = Date.now();
      setElapsed(0);
      stopTimer();
      recTimer.current = setInterval(() => {
        setElapsed(Math.floor((Date.now() - recStart.current) / 1000));
      }, 500);
      setRecording(true);
    } catch (e) {
      const name = e?.name || "";
      console.error("[mic] getUserMedia failed", { name, message: e?.message });
      if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        toast.error(
          isIOS
            ? "Microphone access was denied. On iPhone, in-page recording only works in Safari — open narccheckai.com in Safari, or pick/record an audio file (opening now)."
            : "Microphone blocked. Click the lock/🎤 icon in the address bar, allow Microphone, then tap record again — or upload an audio file (opening now)."
        );
        audioFileRef.current?.click();
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        toast.error("No microphone was found on this device. You can upload an audio file instead.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        toast.error("Your microphone is in use by another app. Close it and try again, or upload an audio file.");
      } else if (name === "OverconstrainedError") {
        toast.error("Your microphone couldn't be used. Please upload an audio file instead.");
      } else {
        toast("Opening your device recorder…");
        audioFileRef.current?.click();
      }
    }
  };

  const stopRecording = () => {
    try {
      mediaRec.current?.stop();
    } catch (e) {
      console.error("[mic] stop failed", e?.name || e);
      setRecording(false);
      stopTimer();
    }
  };

  const discardRecording = () => {
    try {
      if (reviewUrlRef.current) URL.revokeObjectURL(reviewUrlRef.current);
    } catch {}
    reviewUrlRef.current = null;
    setReview(null);
    setElapsed(0);
  };

  const sendForTranscription = async (fileOrBlob, filename) => {
    setTranscribing(true);
    try {
      const { text } = await transcribe(fileOrBlob, filename);
      const cleaned = (text || "").trim();
      if (cleaned) {
        setTranscript((prev) => (prev ? `${prev}\n${cleaned}` : cleaned));
        discardRecording();
        setMode("text");
        toast.success("Transcribed — review & edit below");
      } else {
        toast.error("Couldn't hear anything. Try recording again or upload a clearer file.");
      }
    } catch (e) {
      console.error("[mic] transcription failed", e?.message);
      toast.error(e.message || "Transcription failed");
    } finally {
      setTranscribing(false);
    }
  };

  const analyzeRecording = async () => {
    if (!review?.blob) return;
    await sendForTranscription(review.blob, review.filename);
  };

  const onPickAudio = async (fileList) => {
    const file = Array.from(fileList || [])[0];
    if (!file) return;
    // Route uploaded audio through the same review step (replay / discard / analyse).
    toReview(file, file.name || "recording.m4a");
  };

  const canAnalyze =
    (mode !== "image" && transcript.trim().length > 10) ||
    (mode === "image" && images.length > 0);

  const analyzeNow = async () => {
    if (!canAnalyze || loading) return;
    setLoading(true);
    const nameA = personA.trim() || "Person A";
    const nameB = personB.trim() || "Person B";
    const effectiveMode = mode === "image" ? "image" : "text";
    try {
      const body =
        effectiveMode === "text"
          ? { mode: "text", text: transcript.trim(), person_a_name: nameA, person_b_name: nameB }
          : {
              mode: "image",
              images_base64: images.map((i) => i.base64),
              person_a_name: nameA,
              person_b_name: nameB,
            };
      const result = await analyze(body);
      refresh();
      analysisStore.set({
        result,
        personAName: nameA,
        personBName: nameB,
        sourceMode: effectiveMode,
        sourceTextPreview:
          effectiveMode === "text"
            ? transcript.trim().slice(0, 240)
            : `${images.length} screenshot(s)`,
        savedId: null,
      });
      navigate("/scorecard");
    } catch (e) {
      setLoading(false);
      if (String(e.message).includes("free analyses") || String(e.message).includes("402")) {
        refresh();
        navigate("/paywall");
        return;
      }
      toast.error(e.message || "Analysis failed");
    }
  };

  return (
    <>
      <NavBar />
      {loading && <ScanOverlay />}
      <main className="container-nc" style={{ paddingTop: 44, paddingBottom: 80 }}>
        {/* Hero */}
        <div className="fade-up" style={{ maxWidth: 680 }}>
          <span className="chip chip-brand" data-testid="hero-badge">
            <ShieldAlert size={13} /> Forensic Analysis
          </span>
          <h1
            style={{
              fontSize: "clamp(2.6rem, 6vw, 4.2rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.04em",
              fontWeight: 600,
              margin: "20px 0 14px",
            }}
            data-testid="home-title"
          >
            Who was the{" "}
            <span style={{ color: "var(--brand)" }}>narcissist</span> in that argument?
          </h1>
          <p style={{ color: "var(--muted)", fontSize: 17, lineHeight: 1.6, maxWidth: 560 }}>
            Paste a conversation, drop in screenshots, or record a voice note. NarcCheck AI
            scores each person's manipulation tactics 0–100 and breaks down exactly what happened.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
            {TACTICS.map((t) => (
              <span key={t} className="chip chip-neutral">
                {t}
              </span>
            ))}
          </div>
        </div>

        {/* Workbench */}
        <div
          className="card card-pad fade-up"
          style={{ marginTop: 40, animationDelay: "0.08s" }}
          data-testid="workbench"
        >
          {/* mode switch */}
          <div className="segment" data-testid="mode-segment" style={{ maxWidth: 440 }}>
            {[
              { id: "text", label: "Paste", icon: FileText },
              { id: "image", label: "Screenshots", icon: Images },
              { id: "voice", label: "Voice", icon: Mic },
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                className={`segment-btn ${mode === id ? "active" : ""}`}
                onClick={() => setMode(id)}
                data-testid={`mode-${id}`}
              >
                <Icon size={16} /> {label}
              </button>
            ))}
          </div>

          {/* names */}
          <div className="names-grid" style={{ marginTop: 22 }}>
            <div>
              <label className="label">Person A (you)</label>
              <input
                className="input"
                placeholder="You"
                value={personA}
                onChange={(e) => setPersonA(e.target.value)}
                data-testid="input-person-a"
              />
            </div>
            <div>
              <label className="label">Person B</label>
              <input
                className="input"
                placeholder="Them"
                value={personB}
                onChange={(e) => setPersonB(e.target.value)}
                data-testid="input-person-b"
              />
            </div>
          </div>

          {/* input area */}
          <div style={{ marginTop: 20 }}>
            {mode === "text" && (
              <>
                <label className="label">Transcript</label>
                <textarea
                  className="textarea"
                  placeholder={"You: Why are you always like this?\nThem: Wow. Here you go again, making everything my fault…"}
                  value={transcript}
                  onChange={(e) => setTranscript(e.target.value)}
                  data-testid="input-transcript"
                />
                <div
                  className="mono"
                  style={{ textAlign: "right", color: "var(--muted)", fontSize: 12, marginTop: 6 }}
                >
                  {transcript.length} chars
                </div>
              </>
            )}

            {mode === "image" && (
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => onPickFiles(e.target.files)}
                  data-testid="file-input"
                />
                <div
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => {
                    e.preventDefault();
                    onPickFiles(e.dataTransfer.files);
                  }}
                  data-testid="upload-zone"
                  style={{
                    border: "1px dashed var(--border-strong)",
                    borderRadius: "var(--radius-md)",
                    padding: "44px 20px",
                    textAlign: "center",
                    cursor: "pointer",
                    background: "var(--surface)",
                  }}
                >
                  <div
                    style={{
                      width: 54,
                      height: 54,
                      borderRadius: "50%",
                      background: "var(--brand-tertiary)",
                      display: "grid",
                      placeItems: "center",
                      margin: "0 auto 14px",
                    }}
                  >
                    <CloudUpload size={24} color="var(--brand)" />
                  </div>
                  <div style={{ fontWeight: 500 }}>Drop screenshots here or click to browse</div>
                  <div className="mono" style={{ color: "var(--muted)", fontSize: 12, marginTop: 6 }}>
                    UP TO 6 · JPEG / PNG
                  </div>
                </div>
                {images.length > 0 && (
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 14 }}>
                    {images.map((img, i) => (
                      <div key={i} style={{ position: "relative" }}>
                        <img
                          src={img.url}
                          alt=""
                          style={{
                            width: 92,
                            height: 92,
                            objectFit: "cover",
                            borderRadius: 12,
                            border: "1px solid var(--border)",
                          }}
                        />
                        <button
                          className="btn-icon"
                          onClick={() => setImages((prev) => prev.filter((_, idx) => idx !== i))}
                          data-testid={`remove-image-${i}`}
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 24,
                            height: 24,
                            background: "var(--brand)",
                            borderColor: "transparent",
                            color: "#fff",
                          }}
                        >
                          <X size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {mode === "voice" && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 16,
                  padding: "32px 20px",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                }}
              >
                <input
                  ref={audioFileRef}
                  type="file"
                  accept="audio/*"
                  hidden
                  onChange={(e) => onPickAudio(e.target.files)}
                  data-testid="audio-file-input"
                />

                {review ? (
                  /* ---- Review: replay, analyse or discard ---- */
                  <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }} data-testid="recording-review">
                    <div className="mono" style={{ color: "var(--brand-2)", fontSize: 12, letterSpacing: "0.14em" }}>
                      REVIEW YOUR RECORDING
                    </div>
                    <audio src={review.url} controls data-testid="recording-player" style={{ width: "100%", maxWidth: 360 }} />
                    <div style={{ display: "flex", gap: 12, width: "100%", maxWidth: 360 }}>
                      <button
                        className="btn btn-ghost"
                        style={{ flex: 1 }}
                        onClick={discardRecording}
                        disabled={transcribing}
                        data-testid="discard-recording-btn"
                      >
                        <X size={16} /> Discard
                      </button>
                      <button
                        className="btn btn-primary"
                        style={{ flex: 1 }}
                        onClick={analyzeRecording}
                        disabled={transcribing}
                        data-testid="analyze-recording-btn"
                      >
                        {transcribing ? <Loader2 size={16} className="spin" /> : "Transcribe"}
                      </button>
                    </div>
                    <span className="mono" style={{ color: "var(--muted)", fontSize: 11 }}>
                      {transcribing ? "TRANSCRIBING…" : "PLAY TO CHECK, THEN TRANSCRIBE"}
                    </span>
                  </div>
                ) : (
                  <>
                    <button
                      className="btn-icon"
                      onClick={recording ? stopRecording : startRecording}
                      disabled={transcribing}
                      data-testid="record-btn"
                      style={{
                        width: 76,
                        height: 76,
                        background: recording ? "var(--brand)" : "var(--brand-tertiary)",
                        borderColor: "var(--brand)",
                        color: recording ? "#fff" : "var(--brand)",
                      }}
                    >
                      {recording ? <Square size={24} fill="currentColor" /> : <Mic size={28} />}
                    </button>
                    {recording && (
                      <div className="gauge-num" data-testid="record-timer" style={{ fontSize: 26, color: "var(--brand)" }}>
                        {String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}
                      </div>
                    )}
                    <div className="mono" style={{ color: "var(--muted)", fontSize: 12, letterSpacing: "0.14em" }}>
                      {recording ? "RECORDING — TAP TO STOP" : "TAP TO RECORD A VOICE NOTE"}
                    </div>
                    {!recording && (
                      <button
                        className="link"
                        onClick={() => audioFileRef.current?.click()}
                        data-testid="upload-audio-btn"
                        style={{ background: "none", border: "none", fontSize: 13 }}
                      >
                        Or upload / record an audio file instead
                      </button>
                    )}
                    {!recording && IS_IOS_NON_SAFARI && (
                      <p
                        data-testid="ios-mic-note"
                        style={{ color: "var(--muted)", fontSize: 12, lineHeight: 1.55, textAlign: "center", margin: 0, maxWidth: 320 }}
                      >
                        On iPhone, in-page recording works in <b style={{ color: "var(--on-surface-2)" }}>Safari</b>. In this browser, tap above to pick or record an audio file (e.g. from Voice Memos).
                      </p>
                    )}
                  </>
                )}

                {transcript.trim() && (
                  <textarea
                    className="textarea"
                    style={{ minHeight: 140, width: "100%" }}
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    data-testid="voice-transcript"
                  />
                )}
              </div>
            )}
          </div>

          <button
            className="btn btn-primary btn-lg btn-block"
            style={{ marginTop: 22 }}
            disabled={!canAnalyze || loading}
            onClick={analyzeNow}
            data-testid="analyze-button"
          >
            <GaugeIcon size={19} /> Analyse Argument
          </button>
          <p className="mono" style={{ color: "var(--muted)", fontSize: 11, textAlign: "center", marginTop: 14 }}>
            For personal reflection only · not medical or psychological advice
          </p>
        </div>
      </main>
    </>
  );
}
